// Thread composer: draft, live part/encoding counter, explicit Send (Ctrl+Enter), templates picker.
// Send is disabled with the exact reason whenever texting, the role or consent doesn't allow it.
import { useEffect, useRef, useState } from 'react';
import { FileText, Send } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { type MessageTemplate, MessagingClient, type RenderedTemplate, isApiRequestError } from '../../lib/api.ts';
import { describeError } from '../../lib/errorCopy.ts';
import type { SendResult } from './controller.ts';
import {
  BODY_PROBLEM_TEXT, ENCODING_LABEL, MAX_BODY_LENGTH, type MessagingGate, bodyProblem, composerKey, missingVariables, newIdempotencyKey, readRendered,
  readRows, readTemplate, segmentInfo, segmentLabel, sendErrorText, variableLabel,
} from './model.ts';

/** Unsent drafts per thread, in memory for this tab only (never stored on the device). */
const drafts = new Map<string, string>();

export function draftFor(key: string): string {
  return drafts.get(key) ?? '';
}

/** Keeps a draft for a thread (for example text chosen elsewhere before the composer opens). */
export function rememberDraft(key: string, text: string): void {
  if (text) drafts.set(key, text);
  else drafts.delete(key);
}

export function Composer({ draftKey, blockReason, blockCode, gate, onSend, idPrefix = 'composer' }: {
  draftKey: string;
  /** Why Send is unavailable (shown verbatim); null when sending is allowed. */
  blockReason: string | null;
  blockCode: string | null;
  gate: MessagingGate;
  onSend: (body: string, idempotencyKey: string) => Promise<SendResult>;
  idPrefix?: string;
}) {
  const { announce } = useAnnouncer();
  const [body, setBody] = useState(() => draftFor(draftKey));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  // Reused only for a retry of the same text after an unknown outcome.
  const keyRef = useRef<{ key: string; body: string } | null>(null);
  const info = segmentInfo(body);
  const problem = bodyProblem(body);
  const lastShape = useRef(`${info.segments}|${info.encoding}`);
  const [shapeNote, setShapeNote] = useState('');

  useEffect(() => {
    const shape = `${info.segments}|${info.encoding}`;
    if (shape === lastShape.current) return;
    lastShape.current = shape;
    if (info.segments > 0) setShapeNote(segmentLabel(info));
  }, [info.segments, info.encoding]);

  const change = (next: string) => {
    setBody(next);
    drafts.set(draftKey, next);
    setError(null);
  };

  const disabledReason = blockReason ?? (problem && problem !== 'empty' ? BODY_PROBLEM_TEXT[problem] : null);
  const canSubmit = !blockReason && !problem && !sending;

  const submit = async () => {
    if (!canSubmit) return;
    const key = keyRef.current && keyRef.current.body === body ? keyRef.current.key : newIdempotencyKey();
    keyRef.current = { key, body };
    setSending(true);
    setError(null);
    const result = await onSend(body, key);
    setSending(false);
    if (result.ok) {
      keyRef.current = null;
      drafts.delete(draftKey);
      setBody('');
      announce('Message queued. Its delivery state updates in the conversation.');
      return;
    }
    if (!result.error.outcomeUnknown) keyRef.current = null;
    setError(sendErrorText(result.error, gate));
  };

  const ids = { text: `${idPrefix}-text`, count: `${idPrefix}-count`, reason: `${idPrefix}-reason`, error: `${idPrefix}-error` };
  const describedBy = [ids.count, disabledReason ? ids.reason : null, error ? ids.error : null].filter(Boolean).join(' ');

  return (
    <>
    <form className="composer" aria-label="Write a message" onSubmit={e => { e.preventDefault(); void submit(); }}>
      <div className="field">
        <label htmlFor={ids.text}>Message</label>
        <textarea
          id={ids.text}
          className="composer-text"
          rows={3}
          value={body}
          maxLength={MAX_BODY_LENGTH + 200}
          aria-describedby={describedBy}
          aria-invalid={problem === 'too_long' || problem === 'control_characters' || undefined}
          onChange={e => change(e.target.value)}
          onKeyDown={e => {
            if (composerKey({ key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey, altKey: e.altKey, isComposing: e.nativeEvent.isComposing }) === 'send') {
              e.preventDefault();
              void submit();
            }
          }}
        />
      </div>
      <div className="composer-bar">
        <p id={ids.count} className={`composer-count meta${info.overLimit ? ' tone-end' : ''}`}>
          <span className="composer-parts">{info.segments === 0 ? `0 parts, ${ENCODING_LABEL[info.encoding]}` : segmentLabel(info)}</span>
          <span className="sep" aria-hidden="true" />
          <span>{info.length} of {MAX_BODY_LENGTH} characters</span>
          {info.segments > 0 ? <><span className="sep" aria-hidden="true" /><span>{info.remaining} left in this part</span></> : null}
          {info.encoding === 'ucs2' ? (
            <span className="composer-ucs2"> Characters outside GSM-7 ({info.nonGsm.join(' ')}) make each part hold 70 characters, or 67 when split.</span>
          ) : null}
        </p>
        <span className="visually-hidden" aria-live="polite">{shapeNote}</span>
        <div className="form-actions">
          <button type="button" className="btn btn-small" onClick={() => setPicking(true)} aria-haspopup="dialog"><Icon icon={FileText} /> Templates</button>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit} aria-describedby={disabledReason ? ids.reason : undefined} aria-busy={sending || undefined}>
            <Icon icon={Send} /> {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
      {disabledReason ? <p id={ids.reason} className="inline-reason composer-reason" data-code={blockCode ?? problem ?? undefined}>{disabledReason}</p> : null}
      {error ? <p id={ids.error} className="field-error" role="alert">{error}</p> : null}
      <p className="meta">Press Ctrl+Enter (Cmd+Enter on Mac) to send. Enter starts a new line.</p>
    </form>
    {/* Outside the form: Enter in a template field must never submit the message. */}
    <TemplatePicker open={picking} onClose={() => setPicking(false)} onUse={text => { change(text); setPicking(false); }} />
    </>
  );
}

export type PickerState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; templates: MessageTemplate[] };

/** Choose a template, fill its variables and preview the server's render before using it. */
export function TemplatePicker({ open, onClose, onUse, client: injected }: { open: boolean; onClose: () => void; onUse: (text: string) => void; client?: MessagingClient }) {
  const ws = useWorkspace();
  const [client] = useState(() => injected ?? new MessagingClient(ws.api));
  const [state, setState] = useState<PickerState>({ status: 'idle' });
  const [chosen, setChosen] = useState<MessageTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<RenderedTemplate | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || state.status !== 'idle') return;
    setState({ status: 'loading' });
    client.templates().then(
      body => {
        const read = readRows(body, readTemplate);
        setState(read ? { status: 'ready', templates: read.rows } : { status: 'error', message: 'The server sent templates this version can’t read.' });
      },
      error => setState({ status: 'error', message: describeError(error, 'Couldn’t load templates').title }),
    );
  }, [open, state.status, client]);

  return (
    <Dialog open={open} title="Templates" onClose={onClose} labelledBy="tpl-title"
      footer={<>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!preview} aria-describedby={!preview ? 'tpl-use-why' : undefined} onClick={() => preview && onUse(preview.body)}>Use this text</button>
      </>}
    >
      <TemplatePickerBody
        state={state}
        chosen={chosen}
        values={values}
        preview={preview}
        rendering={rendering}
        renderError={renderError}
        onRetry={() => setState({ status: 'idle' })}
        onChoose={t => { setChosen(t); setValues({}); setPreview(null); setRenderError(null); }}
        onValue={(name, value) => { setValues(v => ({ ...v, [name]: value })); setPreview(null); }}
        onPreview={async () => {
          if (!chosen) return;
          setRendering(true);
          setRenderError(null);
          try {
            const rendered = readRendered(await client.renderTemplate(chosen.id, values));
            if (!rendered) throw new Error('unreadable');
            setPreview(rendered);
          } catch (error) {
            setRenderError(isApiRequestError(error) ? describeError(error, 'Couldn’t preview this template').title : 'The server sent a preview this version can’t read.');
          } finally {
            setRendering(false);
          }
        }}
      />
    </Dialog>
  );
}

/** The picker's content for one state (exported for render tests). */
export function TemplatePickerBody({ state, chosen, values, preview, rendering, renderError, onRetry, onChoose, onValue, onPreview }: {
  state: PickerState;
  chosen: MessageTemplate | null;
  values: Readonly<Record<string, string>>;
  preview: RenderedTemplate | null;
  rendering: boolean;
  renderError: string | null;
  onRetry: () => void;
  onChoose: (t: MessageTemplate) => void;
  onValue: (name: string, value: string) => void;
  onPreview: () => void;
}) {
  if (state.status === 'idle' || state.status === 'loading') return <p className="meta" role="status">Loading templates…</p>;
  if (state.status === 'error') {
    return (
      <div>
        <p className="field-error" role="alert">{state.message}</p>
        <button type="button" className="btn btn-small" onClick={onRetry}>Try again</button>
      </div>
    );
  }
  if (state.templates.length === 0) return <p className="field-hint">No templates yet. People with template access can add them.</p>;
  const missing = chosen ? missingVariables(chosen, values) : [];
  return (
    <div className="tpl">
      <fieldset className="field-group">
        <legend>Template</legend>
        {state.templates.map(t => (
          <label key={t.id} className="radio tpl-choice">
            <input type="radio" name="tpl" value={t.id} checked={chosen?.id === t.id} onChange={() => onChoose(t)} />
            <span><strong>{t.name}</strong><span className="meta tpl-body">{t.body}</span></span>
          </label>
        ))}
      </fieldset>
      {chosen ? (
        <>
          {chosen.variables.length > 0 ? (
            <fieldset className="field-group">
              <legend>Fill in</legend>
              {chosen.variables.map(name => (
                <div className="field" key={name}>
                  <label htmlFor={`tpl-var-${name}`}>{variableLabel(name)}</label>
                  <input id={`tpl-var-${name}`} value={values[name] ?? ''} maxLength={200} onChange={e => onValue(name, e.target.value)} />
                </div>
              ))}
            </fieldset>
          ) : null}
          <div className="form-actions">
            <button type="button" className="btn btn-small" onClick={onPreview} disabled={missing.length > 0 || rendering} aria-describedby={missing.length > 0 ? 'tpl-missing' : undefined} aria-busy={rendering || undefined}>
              {rendering ? 'Previewing…' : 'Preview'}
            </button>
          </div>
          {missing.length > 0 ? <p id="tpl-missing" className="inline-reason">Fill in {missing.map(variableLabel).join(', ')} to preview.</p> : null}
          {renderError ? <p className="field-error" role="alert">{renderError}</p> : null}
          {preview ? (
            <div className="tpl-preview" aria-label="Preview from the server">
              <p className="msg-quote">{preview.body}</p>
              <p className="meta">{segmentLabel(preview)} (counted by the server)</p>
            </div>
          ) : (
            <p id="tpl-use-why" className="meta">Preview the template to use it. The server fills in the values and counts the parts.</p>
          )}
        </>
      ) : (
        <p id="tpl-use-why" className="meta">Choose a template to fill in.</p>
      )}
    </div>
  );
}
